import React, { useState } from 'react';
import styles from '../../styles/UserPage.module.css';
import SetupGuideModal from './DocsModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatLastSeen = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString();
};

const HardwareSummary = ({ hardware }) => {
  if (!hardware || !Object.keys(hardware).length) return null;
  return (
    <div style={{ marginTop: '0.35rem' }}>
      {Object.entries(hardware).map(([k, v]) => (
        <span
          key={k}
          style={{
            display: 'inline-block',
            fontSize: '0.72rem',
            color: 'var(--secondary-text-color)',
            marginRight: '0.65rem',
          }}
        >
          <strong>{k}:</strong> {String(v)}
        </span>
      ))}
    </div>
  );
};

const ModelChips = ({ models, warmModels }) => {
  if (!models || !models.length) return null;
  const warm = new Set(warmModels || []);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
      {models.map((m) => {
        const isWarm = warm.has(m);
        return (
          <span
            key={m}
            title={
              isWarm
                ? 'Warm — loaded in memory, responds instantly'
                : 'Installed — loads on first use'
            }
            style={{
              padding: '0.15rem 0.5rem',
              background: isWarm ? 'rgba(245, 158, 11, 0.15)' : 'var(--input-background)',
              border: `1px solid ${isWarm ? '#f59e0b' : 'var(--input-border)'}`,
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: 'var(--text-color)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            {isWarm && <span aria-hidden="true">🔥</span>}
            {m}
          </span>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ConnectTab = ({
  devices,
  isLoading,
  onRefresh,
  onCreateToken,
  onRevokeToken,
  isCreating,
  isRevokingId,
}) => {
  const [deviceName, setDeviceName] = useState('');
  const [newToken, setNewToken] = useState(null); // { token_id, token, device_name }
  const [copied, setCopied] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const handleCreate = async () => {
    if (!deviceName.trim()) return;
    const result = await onCreateToken(deviceName.trim());
    if (result) {
      setNewToken(result);
      setDeviceName('');
      setCopied(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback: select the text
    }
  };

  return (
    <div className={styles.section} style={{ marginTop: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1.25rem' }}>
        <div style={{ flex: 1 }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: '0.4rem' }}>
            Stackform Connect
          </h2>
          <p className={styles.sectionDescription} style={{ margin: 0 }}>
            Run LLMs on your own hardware (Apple Silicon, NVIDIA GPU, or Jetson)
            and use them inside Stackform. Generate a token below, install the
            relay agent, then add a <strong>Local (Self-Hosted)</strong> provider
            in API Keys with endpoint <code>connect://&lt;token_id&gt;</code>.
          </p>
        </div>
        <button
          onClick={() => setShowGuide(true)}
          style={{
            flexShrink: 0,
            fontSize: '0.8rem',
            padding: '5px 12px',
            cursor: 'pointer',
            border: '1px solid var(--input-border)',
            borderRadius: '5px',
            background: 'var(--input-background)',
            color: 'var(--secondary-text-color)',
            whiteSpace: 'nowrap',
            marginTop: '0.15rem',
          }}
        >
          Setup Guide
        </button>
      </div>

      {showGuide && (
        <SetupGuideModal onClose={() => setShowGuide(false)} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Generate token form                                                  */}
      {/* ------------------------------------------------------------------ */}
      <div style={{
        background: 'var(--input-background)',
        border: '1px solid var(--input-border)',
        borderRadius: '6px',
        padding: '0.85rem',
        marginBottom: '1.25rem',
      }}>
        <p style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-color)' }}>
          Register a new device
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <input
            type="text"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
            placeholder='Device label (e.g. "Home Server")'
            className={styles.input}
            style={{ width: '100%', boxSizing: 'border-box' }}
            disabled={isCreating}
          />
          <button
            onClick={() => void handleCreate()}
            className={styles.button}
            disabled={isCreating || !deviceName.trim()}
            style={{
              width: '100%',
              backgroundColor: 'var(--button-background)',
              color: 'var(--button-text)',
              opacity: isCreating || !deviceName.trim() ? 0.6 : 1,
              cursor: isCreating || !deviceName.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {isCreating ? 'Generating…' : 'Generate Token'}
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* One-time token display                                               */}
      {/* ------------------------------------------------------------------ */}
      {newToken && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '6px',
          padding: '0.85rem',
          marginBottom: '1.25rem',
        }}>
          <p style={{ fontWeight: 600, fontSize: '0.9rem', color: '#92400e', marginBottom: '0.5rem' }}>
            Copy this token now — it will not be shown again.
          </p>

          <p style={{ fontSize: '0.8rem', color: '#78350f', marginBottom: '0.35rem' }}>
            <strong>Relay token</strong> — paste into the relay agent <code>.env</code> as{' '}
            <code>STACKFORM_CONNECT_TOKEN</code>:
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.65rem' }}>
            <code style={{
              flex: 1,
              padding: '0.4rem 0.6rem',
              background: '#fff',
              border: '1px solid #f59e0b',
              borderRadius: '4px',
              fontSize: '0.78rem',
              wordBreak: 'break-all',
              color: '#1a1a1a',
            }}>
              {newToken.token}
            </code>
            <button
              onClick={() => void handleCopy(newToken.token)}
              style={{
                fontSize: '0.78rem',
                padding: '4px 10px',
                cursor: 'pointer',
                border: '1px solid #f59e0b',
                borderRadius: '4px',
                background: copied ? '#d97706' : '#fff',
                color: copied ? '#fff' : '#92400e',
                whiteSpace: 'nowrap',
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <p style={{ fontSize: '0.8rem', color: '#78350f', marginBottom: '0.35rem' }}>
            <strong>Token ID</strong> — use as endpoint in API Keys:{' '}
            <code>connect://{newToken.token_id}</code>
          </p>
          <code style={{
            display: 'block',
            padding: '0.3rem 0.6rem',
            background: '#fff',
            border: '1px solid #f59e0b',
            borderRadius: '4px',
            fontSize: '0.78rem',
            color: '#1a1a1a',
            marginBottom: '0.65rem',
          }}>
            connect://{newToken.token_id}
          </code>

          <button
            onClick={() => setNewToken(null)}
            style={{
              fontSize: '0.78rem',
              padding: '4px 12px',
              cursor: 'pointer',
              border: '1px solid var(--input-border)',
              borderRadius: '4px',
              background: 'var(--input-background)',
              color: 'var(--text-color)',
            }}
          >
            I've copied it — dismiss
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Device list                                                          */}
      {/* ------------------------------------------------------------------ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
        <p style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-color)', margin: 0 }}>
          Registered devices
        </p>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          style={{
            fontSize: '0.75rem',
            padding: '3px 10px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            border: '1px solid var(--input-border)',
            borderRadius: '4px',
            background: 'var(--input-background)',
            color: 'var(--secondary-text-color)',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {isLoading ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)' }}>Loading devices…</p>
      ) : !devices.length ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--secondary-text-color)' }}>
          No devices registered yet. Generate a token above to get started.
        </p>
      ) : (
        devices.map((device) => (
          <div
            key={device.token_id}
            style={{
              border: '1px solid var(--input-border)',
              borderRadius: '6px',
              padding: '0.7rem 0.85rem',
              marginBottom: '0.5rem',
              background: 'var(--input-background)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span
                    aria-label={device.connected ? 'Connected' : 'Offline'}
                    style={{
                      width: '9px',
                      height: '9px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: device.connected ? '#22c55e' : 'var(--input-border)',
                    }}
                  />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-color)' }}>
                    {device.device_name}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--secondary-text-color)' }}>
                    {device.connected ? 'Connected' : 'Offline'}
                  </span>
                </div>

                <div style={{ fontSize: '0.75rem', color: 'var(--secondary-text-color)', marginTop: '0.2rem' }}>
                  <span>ID: <code>{device.token_id}</code></span>
                  <span style={{ marginLeft: '1rem' }}>Last seen: {formatLastSeen(device.last_seen_at)}</span>
                </div>

                <HardwareSummary hardware={device.hardware} />
                <ModelChips
                  models={device.installed_models}
                  warmModels={device.warm_models}
                />
              </div>

              <button
                onClick={() => onRevokeToken(device.token_id, device.device_name)}
                disabled={isRevokingId === device.token_id}
                style={{
                  fontSize: '0.75rem',
                  padding: '3px 10px',
                  cursor: isRevokingId === device.token_id ? 'not-allowed' : 'pointer',
                  border: '1px solid #e53e3e',
                  borderRadius: '4px',
                  background: 'transparent',
                  color: '#e53e3e',
                  flexShrink: 0,
                  marginLeft: '0.75rem',
                  opacity: isRevokingId === device.token_id ? 0.6 : 1,
                }}
              >
                {isRevokingId === device.token_id ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ConnectTab;
